// Phase 24 M5 — TwoFactorSetup widget.
//
// Three states:
//   idle:     user has 2FA disabled. Click "Activar 2FA" → setup.
//   setup:    waiting for the first TOTP code; show QR + otpauthUrl.
//   done:     2FA enabled, backup codes shown ONCE.
//   active:   2FA was already enabled when the component mounted; show
//             "Desactivar 2FA" path (asks for a current code).
//
// The backup-code panel includes copy-to-clipboard + download-as-txt so
// the user actually keeps them (Leyro UX).

import { useEffect, useState } from 'react';
import axios from 'axios';

import { disable2fa, setup2fa, status2fa, verify2fa } from '../../api/profile';

type State = 'idle' | 'setup' | 'done' | 'active' | 'disabling';

function describeAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { reason?: string; error?: string } | undefined;
    return data?.reason ?? data?.error ?? err.message ?? fallback;
  }
  return fallback;
}

function downloadTxt(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function TwoFactorSetup(): JSX.Element {
  const [state, setState] = useState<State>('idle');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    status2fa()
      .then((s) => {
        if (cancelled) return;
        setRemaining(s.backupCodesRemaining);
        if (s.enabled) setState('active');
      })
      .catch(() => {
        /* swallow — render idle */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSetup(): Promise<void> {
    setError(null);
    setPending(true);
    try {
      const res = await setup2fa();
      setQrDataUrl(res.qrDataUrl);
      setOtpauthUrl(res.otpauthUrl);
      setState('setup');
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido iniciar a configuración.'));
    } finally {
      setPending(false);
    }
  }

  async function onVerify(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await verify2fa(token);
      setBackupCodes(res.backupCodes);
      setState('done');
      setToken('');
    } catch (err) {
      setError(describeAxiosError(err, 'Código inválido.'));
    } finally {
      setPending(false);
    }
  }

  async function onDisable(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await disable2fa(token);
      setState('idle');
      setToken('');
      setBackupCodes(null);
      setRemaining(null);
    } catch (err) {
      setError(describeAxiosError(err, 'Código inválido.'));
    } finally {
      setPending(false);
    }
  }

  function copyBackup(): void {
    if (!backupCodes) return;
    void navigator.clipboard.writeText(backupCodes.join('\n'));
  }

  function downloadBackup(): void {
    if (!backupCodes) return;
    downloadTxt('leyrochain-backup-codes.txt', backupCodes.join('\n'));
  }

  const surface: React.CSSProperties = {
    background: '#FFFFFF',
    border: '1px solid #DDDDDD',
    color: '#222222',
    borderRadius: 'var(--radius-md)',
  };

  return (
    <section className="p-5" style={surface}>
      <h3 className="text-lg font-semibold mb-2">Verificación en dous pasos (2FA)</h3>
      <p className="text-sm mb-4" style={{ color: '#717171' }}>
        Engade un código de 6 díxitos do teu app autenticador (Google Authenticator, Authy,
        1Password) ao iniciar sesión.
      </p>

      {state === 'idle' ? (
        <button
          type="button"
          onClick={onSetup}
          disabled={pending}
          className="px-4 py-2 rounded-md font-semibold disabled:opacity-60"
          style={{ background: '#2D6E1A', color: '#FFFFFF' }}
        >
          {pending ? 'Cargando…' : 'Activar 2FA'}
        </button>
      ) : null}

      {state === 'setup' ? (
        <form onSubmit={onVerify} className="space-y-4">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR para o app autenticador"
              className="border rounded-md"
              style={{ borderColor: '#DDDDDD', width: 200, height: 200 }}
            />
          ) : null}
          {otpauthUrl ? (
            <details>
              <summary className="text-sm cursor-pointer" style={{ color: '#717171' }}>
                Non podes escanear o QR?
              </summary>
              <code className="text-xs block mt-1 break-all">{otpauthUrl}</code>
            </details>
          ) : null}
          <label className="block">
            <span className="text-sm font-medium">Código de 6 díxitos</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              required
              value={token}
              onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
              className="mt-1 w-40 px-3 py-2 rounded-md tracking-[0.5em] text-lg text-center"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DDDDDD',
              }}
            />
          </label>
          {error ? (
            <p role="alert" className="text-sm" style={{ color: '#B23A3A' }}>
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={pending || token.length !== 6}
            className="px-4 py-2 rounded-md font-semibold disabled:opacity-60"
            style={{ background: '#2D6E1A', color: '#FFFFFF' }}
          >
            {pending ? 'Verificando…' : 'Verificar e activar'}
          </button>
        </form>
      ) : null}

      {state === 'done' && backupCodes ? (
        <div>
          <p className="text-sm mb-3">
            <strong>2FA activado.</strong> Garda estes códigos de recuperación; cada un só pode
            usarse unha vez. <em>Non volverán amosarse.</em>
          </p>
          <ul
            className="grid grid-cols-2 gap-2 p-3 rounded-md font-mono text-sm mb-3"
            style={{ background: '#F7F7F7', border: '1px solid #DDDDDD' }}
            aria-label="backupCodes"
          >
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyBackup}
              className="px-3 py-1.5 text-sm rounded-md"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DDDDDD',
              }}
            >
              Copiar
            </button>
            <button
              type="button"
              onClick={downloadBackup}
              className="px-3 py-1.5 text-sm rounded-md"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DDDDDD',
              }}
            >
              Descargar .txt
            </button>
          </div>
        </div>
      ) : null}

      {state === 'active' ? (
        <div>
          <p className="text-sm mb-3">
            <strong>2FA está activado.</strong>{' '}
            {remaining !== null ? `Quédanche ${remaining} código(s) de recuperación.` : ''}
          </p>
          <button
            type="button"
            onClick={() => setState('disabling')}
            className="px-4 py-2 rounded-md font-semibold"
            style={{
              background: '#FFFFFF',
              color: '#B23A3A',
              border: '1px solid #B23A3A',
            }}
          >
            Desactivar 2FA
          </button>
        </div>
      ) : null}

      {state === 'disabling' ? (
        <form onSubmit={onDisable} className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">
              Introduce un código actual (ou un código de recuperación)
            </span>
            <input
              type="text"
              required
              value={token}
              onChange={(e) => setToken(e.target.value.trim())}
              className="mt-1 w-60 px-3 py-2 rounded-md"
              style={{
                background: '#FFFFFF',
                border: '1px solid #DDDDDD',
              }}
            />
          </label>
          {error ? (
            <p role="alert" className="text-sm" style={{ color: '#B23A3A' }}>
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={pending}
            className="px-4 py-2 rounded-md font-semibold disabled:opacity-60"
            style={{ background: '#B23A3A', color: '#FFFFFF' }}
          >
            Confirmar desactivación
          </button>
        </form>
      ) : null}
    </section>
  );
}
